import { apiOrchestrator } from 'services/apiService';

import type { AutomationRuleDTO, ConditionDTO, LogicalOp, Operator } from '../types/dto';

export type CreateRuleBodyGeneral = {
  name: string;
  description?: string;
  is_active: boolean;
  created_by: string;
  organization_uuid: string;
};

export type RuleDTO = {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_by: string;
  organization_uuid: string;
  created_at: string;
  updated_at: string;
  triggers?: any[];
  conditions: Array<{
    id: number;
    uuid: string;
    parent_id: number | null;
    is_group: boolean;
    logical_operator: 'AND' | 'OR' | null;
    operator: 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'NOT_CONTAINS' | 'IN' | 'NOT_IN' | null;
    left_operand: string | null;
    right_operand: string | string[] | null;
    order: number;
  }>;
  actions?: any[];
};

export interface ConditionCreateBody {
  rule_id: number; // << numérico
  parent_id: number | null;
  is_group: boolean;
  order: number;
  logical_operator?: LogicalOp | null;
  operator?: Operator | null;
  left_operand?: string | null;
  right_operand?: string | string[] | null; // se o back preferir CSV, a gente junta
  condition_def_id?: number; // ✅ ID da condition_definition (do trigger)
  extras?: Record<string, any>; // ✅ Dados adicionais (form_uuid, field_uuid, option_id, etc)
}

// ---------- API ----------
export const automationApi = {
  createRule: async (body: CreateRuleBodyGeneral) => {
    const { data } = await apiOrchestrator.post('/automations/rules', body);
    return data as AutomationRuleDTO;
  },

  patchRule: async (
    ruleUuid: string,
    body: Partial<Pick<CreateRuleBodyGeneral, 'name' | 'description' | 'is_active'>>
  ) => {
    const { data } = await apiOrchestrator.patch(`/automations/rules/${ruleUuid}`, body);
    return data as AutomationRuleDTO;
  },

  getRule: async (ruleUuid: string) => {
    const { data } = await apiOrchestrator.get(`/automations/rules/${ruleUuid}`);
    return data as AutomationRuleDTO;
  },

  deleteRule: async (ruleUuid: string) => {
    const { data } = await apiOrchestrator.delete(`/automations/rules/${ruleUuid}`);
    return data;
  },

  // ----- Condições (rotas planas) -----
  createCondition: async (body: {
    rule_id: number;
    parent_id: number | null;
    is_group: boolean;
    order: number;
    logical_operator?: LogicalOp | null;
    operator?: Operator | null;
    left_operand?: string | null;
    right_operand?: string | string[] | null;
    condition_def_id?: number; // ✅ ID da condition_definition
    extras?: Record<string, any>; // ✅ Dados adicionais
  }) => {
    console.log('🔍 Criando condição com body:', body);
    const { data } = await apiOrchestrator.post('/automations/conditions', body);
    console.log('✅ Condição criada:', data);
    return data as ConditionDTO;
  },

  deleteAllConditions: async (ruleUuid: string) => {
    const dto = await apiOrchestrator.get(`/automations/rules/${ruleUuid}`);
    const rule = dto.data as AutomationRuleDTO;

    // Deletar condições em paralelo, ignorando erros 404
    const deletePromises = (rule.conditions ?? []).map(async c => {
      try {
        await apiOrchestrator.delete(`/automations/conditions/${c.uuid}`);
        console.log(`✅ Condição ${c.uuid} deletada com sucesso`);
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.log(`⚠️ Condição ${c.uuid} já foi deletada (404)`);
        } else {
          console.error(`❌ Erro ao deletar condição ${c.uuid}:`, error);
          throw error; // Re-throw se não for 404
        }
      }
    });

    await Promise.allSettled(deletePromises);
  },
};
